/**
 * One function per API endpoint. Screens call these; nothing below this line
 * knows about URLs.
 */
import { api } from "./http";
import { TERMS_VERSION } from "../components/ContributorTerms";
import type {
  AssignmentCard,
  AuthResult,
  ClaimResponse,
  ContentStats,
  Contributor,
  EarningsSummary,
  Gender,
  LedgerResponse,
  Payout,
  PayoutEligibility,
  PayoutDestination,
  PayoutDetailsPayload,
  PayoutDetailsState,
  PayoutList,
  RecordingCard,
  RecordingList,
  RecordingStatus,
  ReviewCard,
  ReviewDecision,
  ReviewQueue,
  ReviewStats,
  ReviewEvent,
  ImportSummary,
  AdminPayoutList,
  InviteCode,
  InviteCodeList,
  InviteStatus,
  ApplicationCard,
  ApplicationList,
  ApplicationPrompt,
  ApplicationStatus,
  AppConfig,
  ContactResult,
  ForgotPasswordResult,
  ResetPasswordResult,
  ContributionList,
  ContributorProfile,
  PayoutStatus,
  RecordingStatus as RecordingStatusFilter,
  Roster,
  RosterFilter,
  RosterSort,
  TokenPair,
  Whoami,
} from "../types/api";

/* ---------------------------------------------------------------- config */

export const appConfig = {
  /** Public: the signup form needs it before anybody has an account. */
  get: () => api.get<AppConfig>("/config", { auth: false }),
};

/* ------------------------------------------------------------------ auth */

export interface SignupPayload {
  name: string;
  email: string;
  password: string;
  phone: string;
  gender: Gender;
  /** Required: what a response pays depends on it. */
  country: string;
  /** Must be true — no account is created without agreeing to the terms. */
  accepted_terms: boolean;
  terms_version?: string;
  invite_code?: string;
  paypal_email?: string;
}

export const auth = {
  signup: (payload: SignupPayload) =>
    api.post<AuthResult>("/auth/signup", payload, { auth: false }),
  login: (identifier: string, password: string) =>
    api.post<AuthResult>("/auth/login", { identifier, password }, { auth: false }),
  refresh: (refresh_token: string) =>
    api.post<TokenPair>("/auth/refresh", { refresh_token }, { auth: false }),
  logout: (refresh_token?: string) =>
    api.post<{ sessions_revoked: number }>(
      "/auth/logout",
      refresh_token ? { refresh_token } : undefined,
    ),
  me: () => api.get<Contributor>("/auth/me"),
  /**
   * Starting a reset. The answer is the same whether or not the address has
   * an account, so there is nothing here to tell them apart by.
   */
  forgotPassword: (email: string) =>
    api.post<ForgotPasswordResult>("/auth/forgot-password", { email }, { auth: false }),

  /** Finishing one. The link works once and ends every session. */
  resetPassword: (token: string, password: string) =>
    api.post<ResetPasswordResult>(
      "/auth/reset-password",
      { token, password },
      { auth: false },
    ),

  /**
   * `email`, when given, is checked against the address an application
   * invite was sent to — so a mismatch shows up before submitting.
   */
  checkInvite: (code: string, email?: string) => {
    const query = new URLSearchParams({ code });
    if (email) query.set("email", email);
    return api.get<{ code: string; valid: boolean; reason?: string; country?: string }>(
      `/auth/invite-code/check?${query}`,
      { auth: false },
    );
  },
};

/* ------------------------------------------------------------ onboarding */

export interface ProfilePayload {
  gender?: Gender;
  paypal_email?: string;
  phone?: string;
  country?: string;
  languages?: string[];
  date_of_birth?: string;
  bio?: string;
}

interface OnboardingStatus {
  onboarding: Contributor["onboarding"];
  contributor: Contributor;
}

export const onboarding = {
  status: () => api.get<OnboardingStatus>("/onboarding/status"),

  /** All of it at once — a half-changed bank record sends money to the
   *  wrong place under the right name. */
  savePayoutDetails: (payload: PayoutDetailsPayload) =>
    api.put<PayoutDetailsState>("/onboarding/payout-details", payload),
  payoutDetails: () => api.get<PayoutDetailsState>("/onboarding/payout-details"),
  /** Deal with it later: they can record, they just cannot redeem yet. */
  skipPayoutDetails: () => api.post<OnboardingStatus>("/onboarding/payout-details/skip"),

  updateProfile: (payload: ProfilePayload) =>
    api.patch<OnboardingStatus>("/onboarding/profile", payload),
  acceptTerms: (terms_version = TERMS_VERSION) =>
    api.post<OnboardingStatus>("/onboarding/terms", { accepted: true, terms_version }),
  complete: () => api.post<OnboardingStatus>("/onboarding/complete"),
};

/* ----------------------------------------------------------- assignments */

export const assignments = {
  claim: (count?: number) => api.post<ClaimResponse>("/assignments/claim", count ? { count } : {}),
  next: () => api.get<AssignmentCard>("/assignments/next"),
  get: (assignmentId: string) => api.get<AssignmentCard>(`/assignments/${assignmentId}`),
  mine: (status?: string) =>
    api.get<AssignmentCard[]>(`/assignments/mine${status ? `?status=${status}` : ""}`),
  skip: (assignmentId: string, reason?: string) =>
    api.post<ClaimResponse>(`/assignments/${assignmentId}/skip`, reason ? { reason } : {}),
};

/* ------------------------------------------------------------ recordings */

export const recordings = {
  submit: (assignmentId: string, blob: Blob, filename: string, durationSeconds: number) => {
    const form = new FormData();
    form.append("assignment_id", assignmentId);
    form.append("duration_seconds", String(Math.round(durationSeconds * 10) / 10));
    // The blob carries its own type, which is what the API validates against.
    form.append("file", blob, filename);
    return api.upload<RecordingCard>("/recordings", form);
  },
  /** A typed answer, on tasks that accept one. Paid the same as a recording. */
  submitText: (assignmentId: string, text: string) =>
    api.post<RecordingCard>("/recordings/text", { assignment_id: assignmentId, text }),

  mine: (params: { status?: RecordingStatus; search?: string; page?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    query.set("page", String(params.page ?? 1));
    query.set("page_size", "50");
    return api.get<RecordingList>(`/recordings/mine?${query}`);
  },
  get: (recordingId: string) => api.get<RecordingCard>(`/recordings/${recordingId}`),
};

/* -------------------------------------------------------------- earnings */

export const earnings = {
  summary: () => api.get<EarningsSummary>("/earnings/summary"),
  ledger: (page = 1) => api.get<LedgerResponse>(`/earnings/ledger?page=${page}&page_size=50`),
};

/* --------------------------------------------------------------- payouts */

export const payouts = {
  eligibility: () => api.get<PayoutEligibility>("/payouts/eligibility"),
  redeem: (payload: { amount_cents?: number; paypal_email?: string; note?: string } = {}) =>
    api.post<Payout>("/payouts/redeem", payload),
  mine: (page = 1) => api.get<PayoutList>(`/payouts/mine?page=${page}`),
  cancel: (payoutId: string) => api.post<Payout>(`/payouts/${payoutId}/cancel`),
};

/* --------------------------------------------------------------- content */

export const content = {
  stats: () => api.get<ContentStats>("/content/stats"),
};

/* --------------------------------------------------------------- reviews */

/**
 * Back-office calls. These take the reviewer's own credentials rather than
 * the contributor session, so every one of them is passed explicitly.
 */
function reviewOptions(creds: { mode: "token" | "adminKey"; value: string }) {
  return creds.mode === "token"
    ? { bearer: creds.value, auth: false }
    : { adminKey: creds.value, auth: false };
}

export type ReviewCreds = { mode: "token" | "adminKey"; value: string };

export const reviews = {
  whoami: (creds: ReviewCreds) => api.get<Whoami>("/reviews/me", reviewOptions(creds)),
  stats: (creds: ReviewCreds) => api.get<ReviewStats>("/reviews/stats", reviewOptions(creds)),
  queue: (creds: ReviewCreds, params: { status?: string; page?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    query.set("page", String(params.page ?? 1));
    query.set("page_size", "25");
    return api.get<ReviewQueue>(`/reviews/queue?${query}`, reviewOptions(creds));
  },
  claim: (creds: ReviewCreds, count = 1) =>
    api.post<ReviewCard[]>("/reviews/claim", { count }, reviewOptions(creds)),
  release: (creds: ReviewCreds, recordingId: string) =>
    api.post<{ released: boolean }>(
      `/reviews/${recordingId}/release`,
      undefined,
      reviewOptions(creds),
    ),
  decide: (
    creds: ReviewCreds,
    recordingId: string,
    payload: {
      decision: ReviewDecision;
      note?: string;
      reason?: string;
      internal_note?: string;
    },
  ) => api.post<ReviewCard>(`/reviews/${recordingId}/decision`, payload, reviewOptions(creds)),
  history: (creds: ReviewCreds, recordingId: string) =>
    api.get<{ events: ReviewEvent[]; total: number }>(
      `/reviews/${recordingId}/history`,
      reviewOptions(creds),
    ),
  retryPublish: (creds: ReviewCreds, recordingId: string) =>
    api.post<unknown>(`/reviews/${recordingId}/publish/retry`, undefined, reviewOptions(creds)),
  retryAllPublishes: (creds: ReviewCreds) =>
    api.post<{ due: number; published: number }>(
      "/reviews/publish/retry-all",
      undefined,
      reviewOptions(creds),
    ),
};

/* ------------------------------------------------------ content admin */

export const contentAdmin = {
  stats: (creds: ReviewCreds) =>
    api.get<ContentStats>("/admin/content/stats", reviewOptions(creds)),

  /**
   * Upload the content sheet. Defaults to a dry run — the file is parsed and
   * summarised, and nothing is written until `dryRun` is false.
   */
  importSheet: (
    creds: ReviewCreds,
    file: File,
    options: { dryRun?: boolean; sheet?: string; deactivateMissing?: boolean } = {},
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("dry_run", String(options.dryRun ?? true));
    form.append("deactivate_missing", String(options.deactivateMissing ?? false));
    if (options.sheet) form.append("sheet", options.sheet);
    return api.upload<ImportSummary>("/admin/content/import", form, reviewOptions(creds));
  },
};

/* ------------------------------------------------------- payouts admin */

export const payoutAdmin = {
  /**
   * The full bank details for one payout. Fetched per payout when somebody
   * is actually about to send it, never with the queue — so a screenshot of
   * the queue is not a list of bank accounts.
   */
  destination: (creds: ReviewCreds, payoutId: string) =>
    api.get<PayoutDestination>(
      `/admin/payouts/${encodeURIComponent(payoutId)}/destination`,
      reviewOptions(creds),
    ),

  queue: (creds: ReviewCreds, params: { status?: string; page?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    query.set("page", String(params.page ?? 1));
    query.set("page_size", "25");
    return api.get<AdminPayoutList>(`/admin/payouts?${query}`, reviewOptions(creds));
  },

  /**
   * Move a payout on. `paid` is the point of no return — it moves the money
   * out of reserved for good — so the UI asks for the PayPal reference.
   */
  setStatus: (
    creds: ReviewCreds,
    payoutId: string,
    payload: { status: PayoutStatus; provider_reference?: string; failure_reason?: string },
  ) => api.post<Payout>(`/admin/payouts/${payoutId}/status`, payload, reviewOptions(creds)),
};

/* ------------------------------------------------------- invites admin */

export const inviteAdmin = {
  list: (
    creds: ReviewCreds,
    params: { status?: InviteStatus; search?: string; page?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    query.set("page", String(params.page ?? 1));
    query.set("page_size", "50");
    return api.get<InviteCodeList>(`/admin/invite-codes?${query}`, reviewOptions(creds));
  },

  /**
   * Mint codes. `max_uses` above 1 makes one code a whole group can use,
   * rather than one code each.
   */
  mint: (
    creds: ReviewCreds,
    payload: { count: number; max_uses?: number; expires_days?: number; note?: string },
  ) => api.post<{ codes: string[] }>("/admin/invite-codes", payload, reviewOptions(creds)),

  /** Withdraw a code, or put it back with `revoked: false`. */
  setRevoked: (creds: ReviewCreds, code: string, revoked: boolean) =>
    api.post<InviteCode>(
      `/admin/invite-codes/${encodeURIComponent(code)}/revoke`,
      { revoked },
      reviewOptions(creds),
    ),
};

/* ---------------------------------------------------------- applying */

export interface ApplicationPayload {
  name: string;
  email: string;
  /** Required: it sets the rate, and the invite is issued for it. */
  country: string;
  promptId: string;
  blob: Blob;
  filename: string;
  durationSeconds: number;
  source?: string;
  /** The honeypot. A person never sees the field, so it is always empty. */
  website?: string;
}

/** Public: whoever followed the link has no account yet. */
export const applications = {
  prompt: () => api.get<ApplicationPrompt>("/applications/prompt", { auth: false }),
  submit: (payload: ApplicationPayload) => {
    const form = new FormData();
    form.append("name", payload.name);
    form.append("email", payload.email);
    form.append("country", payload.country);
    form.append("prompt_id", payload.promptId);
    form.append("duration_seconds", String(Math.round(payload.durationSeconds * 10) / 10));
    if (payload.source) form.append("source", payload.source);
    form.append("website", payload.website ?? "");
    form.append("file", payload.blob, payload.filename);
    return api.upload<{ application_id: string; status: ApplicationStatus }>(
      "/applications",
      form,
      { auth: false },
    );
  },
};

/* --------------------------------------------------- applications admin */

export const applicationAdmin = {
  list: (creds: ReviewCreds, params: { status?: ApplicationStatus; page?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    query.set("page", String(params.page ?? 1));
    query.set("page_size", "25");
    return api.get<ApplicationList>(`/admin/applications?${query}`, reviewOptions(creds));
  },

  /** Both outcomes email the applicant, so the screen confirms before calling this. */
  decide: (
    creds: ReviewCreds,
    applicationId: string,
    payload: { decision: "accepted" | "rejected"; note?: string },
  ) =>
    api.post<ApplicationCard>(
      `/admin/applications/${applicationId}/decision`,
      payload,
      reviewOptions(creds),
    ),

  resend: (creds: ReviewCreds, applicationId: string) =>
    api.post<ApplicationCard>(
      `/admin/applications/${applicationId}/resend`,
      undefined,
      reviewOptions(creds),
    ),
};

/* ------------------------------------------------------- roster admin */

/**
 * The contributor roster. Read-only: the levers that act on somebody —
 * suspend, review, pay — already live on their own calls.
 */
export const rosterAdmin = {
  list: (
    creds: ReviewCreds,
    params: {
      status?: RosterFilter;
      country?: string;
      search?: string;
      sort?: RosterSort;
      page?: number;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.country) query.set("country", params.country);
    if (params.search) query.set("search", params.search);
    query.set("sort", params.sort ?? "joined");
    query.set("page", String(params.page ?? 1));
    query.set("page_size", "25");
    return api.get<Roster>(`/admin/contributors?${query}`, reviewOptions(creds));
  },

  /**
   * One contributor in full. Audio comes back signed only for callers who
   * may also read recordings, so finance sees the tally without the takes.
   */
  profile: (creds: ReviewCreds, contributorId: string, recent = 10) =>
    api.get<ContributorProfile>(
      `/admin/contributors/${encodeURIComponent(contributorId)}/profile?recent=${recent}`,
      reviewOptions(creds),
    ),

  /** Everything they have recorded, newest first. */
  contributions: (
    creds: ReviewCreds,
    contributorId: string,
    params: { status?: RecordingStatusFilter; page?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    query.set("page", String(params.page ?? 1));
    query.set("page_size", "25");
    return api.get<ContributionList>(
      `/admin/contributors/${encodeURIComponent(contributorId)}/contributions?${query}`,
      reviewOptions(creds),
    );
  },

  /** Suspend or reactivate. The only write on this screen. */
  setStatus: (creds: ReviewCreds, contributorId: string, status: "active" | "suspended") =>
    api.post<{ contributor_id: string; status: string }>(
      `/admin/contributors/${encodeURIComponent(contributorId)}/status?status=${status}`,
      undefined,
      reviewOptions(creds),
    ),
};

/* --------------------------------------------------------------- contact */

/**
 * Open to anybody, signed in or not. A lapsed session must not block the
 * message, so this call never sends a contributor token.
 */
export const contact = {
  send: (payload: { name: string; email: string; message: string; website?: string }) =>
    api.post<ContactResult>("/contact", { website: "", ...payload }, { auth: false }),
};
